import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { HelpCircleIcon, AlertCircleIcon } from 'lucide-react';

import { companyLocationsApi } from 'api';
import LoadingSpinner from 'components/common/loaders/LoadingSpinner';
import { ComboBoxField, ComboBoxFieldProps } from '@epoch-ui/components/Form';
import { Tooltip, TooltipTrigger, TooltipContent } from '@epoch-ui/components/Tooltip';

type EventLocationFieldProps = Omit<ComboBoxFieldProps, 'children' | 'triggerContent'>;

const Label = (): React.ReactNode => {
  return (
    <>
      Location <span className="tw-text-muted-foreground">(optional)</span>&nbsp;
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" aria-label="Location selection help" className="tw-m-0  tw-p-0">
            <HelpCircleIcon size={13} />
          </button>
        </TooltipTrigger>
        <TooltipContent className="tw-max-w-80">
          <p>
            Location can be used if your event is hosted for a geographic region or office.
            Selecting a location helps employees search for events and groups data for reporting.
          </p>
          <a
            target="_blank"
            rel="noreferrer"
            className="tw-text-primary-foreground tw-underline hover:tw-text-primary-foreground"
            href="https://help.epochapp.com/articles/what-is-an-office"
          >
            Learn more
          </a>
        </TooltipContent>
      </Tooltip>
    </>
  );
};

const EventLocationField: React.FC<EventLocationFieldProps> = (props) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['eventEditor', 'companyLocations'],
    queryFn: () => companyLocationsApi.list(),
    throwOnError: false
  });

  const getDisplayValue = React.useCallback(
    (locationId?: string) => {
      return locationId ? (
        <span className="tw-text-foreground">
          {
            data?.companyLocations.find((location) => String(location.id) === String(locationId))
              ?.name
          }
        </span>
      ) : (
        'Select'
      );
    },
    [data]
  );

  return (
    <ComboBoxField
      label={<Label />}
      triggerContent={getDisplayValue}
      contentAvoidCollisions
      {...props}
    >
      <ComboBoxField.Command>
        <ComboBoxField.Input placeholder="Search" />
        <ComboBoxField.List className="tw-mt-3">
          {isLoading && <LoadingSpinner />}
          {error && (
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-destructive-foreground">
              <AlertCircleIcon size={16} />
              <div>Error while loading locations</div>
            </div>
          )}
          {!isLoading && !error && <ComboBoxField.Empty>No location found</ComboBoxField.Empty>}
          <ComboBoxField.Group>
            {data?.companyLocations.map((location) => (
              <ComboBoxField.Item key={location.id} value={location.id.toString()}>
                {location.name}
              </ComboBoxField.Item>
            ))}
          </ComboBoxField.Group>
        </ComboBoxField.List>
      </ComboBoxField.Command>
    </ComboBoxField>
  );
};

export { EventLocationField };
